import { useEffect, useState, type RefObject } from 'react';

/**
 * The width below which the home's two-column rows stack.
 *
 * **This number lives in three places and they must agree**: the `@container` queries in
 * `DesktopHomeHeader.module.css` and `CatchUpSections.module.css`, and here. CSS cannot read a TS
 * constant, so `test/home-reflows-with-the-pane.test.ts` asserts the CSS matches this value.
 *
 * At 700 the ⅓ column is ~223px — about the narrowest the QR block and the Files card read at.
 */
export const HOME_STACK_BREAKPOINT_PX = 700;

/**
 * `useNarrow`, but measuring an ELEMENT instead of the window.
 *
 * The home needs this because the thing that narrows it is the docked chat, which takes 370px out of
 * the content pane through `--chat-w` and leaves `window.innerWidth` untouched — so a `matchMedia`
 * query cannot see it (Monday 12734251677). Anything whose rendering is coupled to a row that stacks
 * on a `@container` query has to observe the same box the query does, or the two disagree: the row
 * restacks to one column while its contents keep rendering their full-width desktop form.
 *
 * Returns `false` until the first observation, matching `useNarrow`'s pre-effect default, so SSR and
 * the first client paint agree on the desktop rendering.
 */
export function useContainerNarrow(ref: RefObject<HTMLElement | null>, threshold: number): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // ResizeObserver rather than a resize listener: the pane's width changes when the chat docks or
    // the rail collapses, neither of which resizes the window.
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentBoxSize?.[0]?.inlineSize ?? entry?.contentRect.width;
      if (width !== undefined) setNarrow(width < threshold);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return narrow;
}
